import { useEffect, useRef, type PointerEvent } from "react";

/** Only documents at least this many screens long get the thumb. */
const MIN_SCREENS = 3;
const MIN_THUMB_PX = 48;
const HIDE_AFTER_MS = 1500;

/**
 * A draggable scroll thumb for touch screens, where the browser's scrollbar can't be
 * grabbed. It appears on the right edge while the page scrolls and fades out after;
 * dragging it jumps through a long document. Updated through the DOM, not React state,
 * so scrolling never re-renders anything.
 */
export function ScrollThumb() {
  const track = useRef<HTMLDivElement>(null);
  const thumb = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; grab: number } | null>(null);

  useEffect(() => {
    const t = track.current!;
    const el = document.scrollingElement ?? document.documentElement;
    let frame = 0;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    const layout = () => {
      const range = el.scrollHeight - window.innerHeight;
      const long = el.scrollHeight >= window.innerHeight * MIN_SCREENS;
      const h = t.clientHeight;
      const size = Math.max(MIN_THUMB_PX, (h * window.innerHeight) / el.scrollHeight);
      const top = range > 0 ? (window.scrollY / range) * (h - size) : 0;
      thumb.current!.style.height = `${size}px`;
      thumb.current!.style.transform = `translateY(${Math.min(Math.max(top, 0), h - size)}px)`;
      return long;
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!layout()) return t.classList.remove("visible");
        t.classList.add("visible");
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          if (!drag.current) t.classList.remove("visible");
        }, HIDE_AFTER_MS);
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
      clearTimeout(hideTimer);
    };
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!track.current!.classList.contains("visible")) return;
    // Don't take focus from the editor (and close the keyboard).
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointerId: e.pointerId, grab: e.clientY - thumb.current!.getBoundingClientRect().top };
    track.current!.classList.add("dragging");
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== e.pointerId) return;
    const el = document.scrollingElement ?? document.documentElement;
    const rect = track.current!.getBoundingClientRect();
    const size = thumb.current!.offsetHeight;
    const fraction = (e.clientY - drag.current.grab - rect.top) / (rect.height - size);
    window.scrollTo({ top: Math.min(Math.max(fraction, 0), 1) * (el.scrollHeight - window.innerHeight), behavior: "instant" });
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== e.pointerId) return;
    drag.current = null;
    track.current!.classList.remove("dragging");
    // Let it fade out as after any scroll.
    window.dispatchEvent(new Event("scroll"));
  };

  return (
    // A touch shortcut: screen readers and keyboards have their own ways to move through the text.
    <div className="scroll-track" ref={track} aria-hidden="true">
      <div
        className="scroll-thumb"
        ref={thumb}
        data-testid="scroll-thumb"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}
